import { Immutable } from "immer";
import React from "react";
import {
    FinancingModalTriggerFinance,
    financingCCs,
} from "tsi-common-react/src/apps/checkout/FinancingModalTriggerFinance/index";
import { FinanceCalculator } from "tsi-common-react/src/apps/financing/FinanceCalculator";
import { FinancingModalTrigger } from "tsi-common-react/src/apps/financing/FinancingModalTrigger";
import { PromotionBlock } from "tsi-common-react/src/components/PromotionBlock/index";
import { jt, t } from "ttag";

import { templates } from "@reactivated";

import { FinancingModalTriggerCheckNow } from "../../apps/checkout/FinancingModalTriggerCheckNow";
import { DistanceToStoreSSR } from "../../apps/retail-locator/DistanceToStoreSSR";
import { TempurFinanceFAQsBlock } from "../../blocks/TempurFinanceFAQsBlock";
import { TempurFinanceOptionsBlock } from "../../blocks/TempurFinanceOptionsBlock";

/**
 * Full-page React assembly for `FinancePage` (#33578, Phase 2 FINAL).
 *
 * ONE assembly serves BOTH tempurpedic finance-page deployments (the default
 * and the sale2 outlet). It is composed as a sibling inside the full-document
 * `FinancePageTemplate` shell; the `variant` ("default" | "sale2") is resolved
 * server-side from `APP_SLUG` in `FinancePage._render_react` and threaded
 * through (the two legacy per-deployment Django templates -- the default
 * `finance_page.html` and the sale2 `templates-alt` override -- are deleted).
 * The legacy `{% block layout %}` bodies rendered the `finance_fields`
 * StreamField in `block_type`-filtered `{% for %}` loops interleaved with
 * static chrome and the financing `include_react` widgets:
 *   - `finance_options` -> table row     (TempurFinanceOptionsBlock)
 *   - `promotion`       -> FAQ row, BEFORE the FAQs header, each wrapped in
 *                          `div.ohter-financing` [sic] (DEFAULT VARIANT ONLY;
 *                          the sale2 template had NO promotion loop)
 *   - `faqs`            -> FAQs row      (TempurFinanceFAQsBlock)
 *
 * Variant divergences reproduced from the legacy templates:
 *   - hero: default sets `background-image` on `.finance-hero`; sale2 renders
 *     a `.finance-hero__image > img` instead (the legacy `alt="{{
 *     small_image.get_title }}"` resolved an undefined variable -> alt="").
 *   - benefits item 4 copy differs ("TempurPedic.com" vs "TempurPedic.com &
 *     Tempurpedicoutlet.com"); the other four items are shared.
 *   - "3 Easy Ways" (default; `.finance-use` columns incl. the store column
 *     with DistanceToStore) vs "2 Easy Ways" (sale2;
 *     `.finance-use--two-columns`, no store column). The sale2 shopping
 *     column's screenreader description says "Tempurpedicsale.com" while its
 *     visible h4 says "Tempurpedicoutlet.com" -- a legacy copy quirk,
 *     preserved verbatim.
 *
 * The legacy `{% include 'cms/components/svg.html' %}` icon includes rendered
 * a screenreader span + an inlined SVG from the build-generated
 * `static/dist2/svg-django/{webpage,phone,store-door}.svg` Django templates
 * (esbuild `copyDjangoSVGs` output of `client/src/svg/*.svg`). Those files
 * are templates with `{{ class }}` / `{% if %}` placeholders, so they cannot
 * be fetched client-side (react-inlinesvg would render the raw tags); the
 * exact rendered output (svgo-optimized paths + the attrs svg.html
 * substitutes for this page's include args) is reproduced inline below.
 *
 * The financing widgets that were separate `include_react` calls are rendered
 * as their React components directly (no nested `include_react`), passing the
 * same props the original `include_react` calls did:
 *   - FinancingCheckNow -> FinancingModalTriggerCheckNow (tempurpedic-local
 *     implementation of the common `FinancingCheckNowTemplate`); `classString`
 *     == legacy `extra_classes`. As on sealy, the hero trigger is NOT wrapped
 *     in `.financing-modal-trigger`.
 *   - FinancingForm -> FinancingModalTrigger modalType="full-app"
 *     (mirrors `FinancingFormTemplate`): className/title/applicationSource +
 *     children == legacy link_class/link_title/application_source/link_text.
 *   - FinanceCalculator -> FinanceCalculator (mirrors
 *     `FinanceCalculatorTemplate`).
 *   - DistanceToStore -> DistanceToStoreSSR (the tempurpedic implementation
 *     behind `DistanceToStoreTemplate`; default variant only).
 *   - FinancingTriggerFinance -> FinancingModalTriggerFinance ccSource=tempur
 *     (mirrors `FinancingTriggerFinanceTemplate`): cc_source="tempur",
 *     application_source="Financing Page".
 *
 * GA4 parity for promotions: NO `blockIndex` (defaults to 1). The legacy loop
 * rendered each promotion via `{% include_block block %}` ->
 * promotion-block.html -> PromotionBlockTemplate, which passes no index, so
 * EVERY promotion on this page emitted `creative_slot: "promotion-block-1"`
 * regardless of position (adjudicated during the #33578 sealy review).
 *
 * Page-level data the client side does not otherwise have (default plan
 * terms, `|richtext`-expanded headline, the pre-approval disclosure HTML, the
 * hero image URL, the shop-mattresses slugurl, the sleep-expert phone number)
 * is threaded by the `get_finance_page_body` templatetag.
 */

const APPLICATION_SOURCE = "Financing Page";

type IProps = Immutable<
    Omit<templates.FinancePageTemplate, "is_preview" | "page_chrome">
>;

type FinanceField = IProps["page"]["finance_fields"]["value"][number];
type FinanceOptionsField = Extract<FinanceField, { type: "finance_options" }>;
type PromotionField = Extract<FinanceField, { type: "promotion" }>;
type FAQsField = Extract<FinanceField, { type: "faqs" }>;

type IconProps = Immutable<{
    description: string;
    title: string;
}>;

/**
 * Exact render of `cms/components/svg.html` with
 * `src='webpage' class='finance-use__icon' ariaHidden='true'`:
 * screenreader span + the svgo-built `svg-django/webpage.svg`.
 */
const WebpageIcon = (props: IconProps): React.JSX.Element => (
    <>
        <span className="ada-screenreader-only">{props.description}</span>
        <svg
            className="finance-use__icon"
            role="img"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            xmlSpace="preserve"
            viewBox="0 0 60.1 47.5"
        >
            <title>{props.title}</title>
            <path d="M14 9.9c-1.5 0-2.7-1.2-2.7-2.7s1.2-2.7 2.7-2.7 2.7 1.2 2.7 2.7-1.3 2.7-2.7 2.7m0-3.4c-.4 0-.7.3-.7.7s.3.7.7.7.7-.3.7-.7-.4-.7-.7-.7" />
            <path d="M60.1 12h-.2V4c0-2.2-1.8-4-4-4H4C1.8 0 0 1.8 0 4v39.5c0 2.2 1.8 4 4 4h51.9c2.2 0 4-1.8 4-4v-1.6h.2zm-2.2 27.9H2V14h55.9zM2 4c0-1.1.9-2 2-2h51.9c1.1 0 2 .9 2 2v8H2zm55.9 39.5c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2v-1.6h55.9z" />
            <path d="m34.4 22.3.2 9.5 2.3-2.5 2.6 4.5 1.7-1.1-2.6-4.5 2.9-.9zM7.2 9.9c-1.5 0-2.7-1.2-2.7-2.7s1.2-2.7 2.7-2.7 2.7 1.2 2.7 2.7-1.2 2.7-2.7 2.7m0-3.4c-.4 0-.7.3-.7.7s.3.7.7.7.7-.3.7-.7-.3-.7-.7-.7m13.5 3.4c-1.5 0-2.7-1.2-2.7-2.7s1.2-2.7 2.7-2.7 2.7 1.2 2.7 2.7-1.2 2.7-2.7 2.7m0-3.4c-.4 0-.7.3-.7.7s.3.7.7.7.7-.3.7-.7-.3-.7-.7-.7" />
        </svg>
    </>
);

/** Exact render of svg.html with `src='phone'` (see WebpageIcon). */
const PhoneIcon = (props: IconProps): React.JSX.Element => (
    <>
        <span className="ada-screenreader-only">{props.description}</span>
        <svg
            className="finance-use__icon"
            role="img"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            xmlSpace="preserve"
            viewBox="0 0 41.2 41.5"
        >
            <title>{props.title}</title>
            <path d="M31.3 41.3c-1.4 0-2.9-.4-4.2-1.2l-3.9-2.3C15 33.1 8.2 26.3 3.5 18.1l-2.3-4C-.5 11.1 0 7 2.6 4.3L5.8 1l.1-.1c.6-.5 1.3-.8 2-.8.8 0 1.5.4 2 .9l6.3 6.4c1 1.1 1 2.9 0 4l-3.1 3.1c-.2.2-.2.5-.1.7l.9 1.4c2.6 4.4 6.3 8.1 10.7 10.7l1.6.9c.3.2.5.1.7-.1L30 25c1.1-1.1 2.9-1.1 4 0l6.4 6.4c1.1 1.1 1.1 2.9 0 4l-3.3 3.3c-1.5 1.8-3.6 2.6-5.8 2.6M7.8 2.1c-.2 0-.4.1-.6.2L4 5.7c-1.9 2-2.4 5.1-1 7.6l2.3 4c4.5 7.9 11.1 14.4 18.9 18.9l4 2.3c2.4 1.5 5.6 1.1 7.5-1l3.3-3.3c.3-.3.3-.9 0-1.2l-6.3-6.3c-.3-.3-.9-.3-1.2 0l-3 3c-.8.8-2.1 1-3.2.4l-1.6-.9c-4.7-2.8-8.6-6.7-11.4-11.4l-.9-1.5c-.6-1-.4-2.3.3-3.1l3.1-3.1c.3-.3.3-.9 0-1.2L8.5 2.4c-.2-.1-.4-.2-.7-.3.1 0 0 0 0 0" />
        </svg>
    </>
);

/** Exact render of svg.html with `src='store-door'` (see WebpageIcon). */
const StoreDoorIcon = (props: IconProps): React.JSX.Element => (
    <>
        <span className="ada-screenreader-only">{props.description}</span>
        <svg
            className="finance-use__icon"
            role="img"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            xmlSpace="preserve"
            viewBox="0 0 42 57.8"
        >
            <title>{props.title}</title>
            <path d="M0 14.9v42.9h42V14.9zm2 2h18.4v38.9H2zm38 38.9H22.4V16.9H40zM0 0v11.4h42V0zm40 9.4H2V2h38z" />
            <path d="M24.9 35.3H31v2h-6.1zm-13 0H18v2h-6.1z" />
        </svg>
    </>
);

export const TempurFinancePageBody = (props: IProps): React.JSX.Element => {
    const isSale2 = props.variant === "sale2";
    const applyNowText = t`Apply Now`;
    const fields = props.page.finance_fields.value;

    // Filter the single StreamField value into the sections, mirroring the
    // legacy `{% for block in self.finance_fields %}` / `{% if
    // block.block_type == ... %}` loops.
    const financeOptions = fields.filter(
        (b): b is FinanceOptionsField => b.type === "finance_options",
    );
    const promotions = fields.filter(
        (b): b is PromotionField => b.type === "promotion",
    );
    const faqs = fields.filter((b): b is FAQsField => b.type === "faqs");

    // Per-variant copy (see module docstring).
    const easyWaysHeader = isSale2
        ? t`2 Easy Ways to Use Your Approved Tempur-Pedic Account Immediately`
        : t`3 Easy Ways to Use Your Approved Tempur-Pedic Account Immediately`;
    const financeUseClass = isSale2
        ? "finance-use finance-use--two-columns"
        : "finance-use";
    const shoppingDescription = isSale2
        ? t`Shopping Online at Tempurpedic.com & Tempurpedicsale.com`
        : t`Shopping Online at Tempurpedic.com`;
    const shoppingTitle = isSale2
        ? t`Shopping Online at Tempurpedic.com & Tempurpedicoutlet.com`
        : t`Shopping Online at Tempurpedic.com`;
    const shoppingIconTitle = t`Shopping Icon`;
    const talkToDescription = t`Talk to a Sleep Expert`;
    const phoneIconTitle = t`Phone Icon`;
    const visitClosestDescription = t`Visit closest Tempur-Pedic Store`;
    const locationIconTitle = t`Location Icon`;

    // Interpolated link for the Wells Fargo `jt` sentence below.
    const wellsFargoLink = (
        <a key="wf" href="https://wellsfargo.com/cardholders">
            wellsfargo.com/cardholders
        </a>
    );

    return (
        <>
            <div
                className="hero finance-hero l-full-width u-margin-bottom--xl"
                style={
                    !isSale2 && props.hero_image_url
                        ? {
                              backgroundImage: `url(${props.hero_image_url})`,
                          }
                        : undefined
                }
            >
                {isSale2 && props.hero_image_url && (
                    <div className="finance-hero__image">
                        {/* The legacy sale2 alt referenced an undefined
                            `small_image` variable -> rendered alt="". */}
                        <img
                            src={props.hero_image_url}
                            alt=""
                            fetchPriority="high"
                        />
                    </div>
                )}
                <div className="finance-hero__content l-capped-width">
                    <div
                        className="finance-hero__copy"
                        dangerouslySetInnerHTML={{
                            __html: props.headline_html,
                        }}
                    />
                </div>
                <FinancingModalTriggerCheckNow classString="button finance-hero__button finance-hero__button--pre-qual al-financing-page__hero--check-now" />
            </div>

            <div className="finance-page-row l-capped-width u-margin-bottom--l">
                <div className="finance-page-offer">
                    <div className="finance-page-offer__inner-container">
                        <h1 className="finance-page-offer__terms">
                            <span className="finance-page-offer__terms-line finance-page-offer__terms-line--1">
                                {props.apr}% APR
                            </span>
                            <span className="finance-page-offer__terms-line finance-page-offer__terms-line--2">
                                {t`for`}{" "}
                                <span className="finance-page-offer__line-rule" />
                            </span>
                            <span className="finance-page-offer__terms-line finance-page-offer__terms-line--3">
                                {props.term_months} {t`Months`}
                                <sup>
                                    <a href="#terms_and_conditions">
                                        {props.fine_print_superscript}
                                        <span className="ada-screenreader-only">
                                            {t`Additional information about Terms and Conditions`}
                                        </span>
                                    </a>
                                </sup>
                            </span>
                        </h1>
                        <div className="finance-page-offer__buttons">
                            <FinancingModalTrigger
                                modalType="full-app"
                                className="button finance-page-offer__button finance-page-offer__button--apply"
                                title={applyNowText}
                                applicationSource={APPLICATION_SOURCE}
                            >
                                {applyNowText}
                            </FinancingModalTrigger>
                            <a
                                className="finance-page-offer__button finance-page-offer__button--terms"
                                href="#terms_and_conditions"
                            >
                                {t`Financial Terms`}
                                <span className="ada-screenreader-only">
                                    {t`Additional information about Terms and Conditions`}
                                </span>
                            </a>
                        </div>
                    </div>
                </div>
                <div className="finance-page-benefits">
                    <ul className="finance-page-benefits__list">
                        <li className="finance-page-benefits__list-item">
                            {t`No annual fee`}
                            <sup>
                                <a href="#terms_and_conditions">
                                    1
                                    <span className="ada-screenreader-only">
                                        {t`Additional information about Terms and Conditions`}
                                    </span>
                                </a>
                            </sup>
                        </li>
                        <li className="finance-page-benefits__list-item">
                            {t`Quick and secure online application`}
                        </li>
                        <li className="finance-page-benefits__list-item">
                            {t`Apply now, or in checkout`}
                        </li>
                        <li className="finance-page-benefits__list-item">
                            {isSale2
                                ? t`For use on TempurPedic.com & Tempurpedicoutlet.com, by phone with a sleep expert, or in a Tempur-Pedic flagship store.`
                                : t`For use on TempurPedic.com, by phone with a sleep expert, or in a Tempur-Pedic flagship store.`}
                        </li>
                        <li className="finance-page-benefits__list-item">
                            {t`The Tempur-Pedic Credit Card cannot be used for purchases at any other merchants`}
                        </li>
                    </ul>
                </div>
            </div>

            <div className="finance-page-row l-capped-width u-margin-bottom--l">
                <div className="finance-table">
                    {financeOptions.map((block) => (
                        <TempurFinanceOptionsBlock
                            key={block.id}
                            value={block.value}
                        />
                    ))}
                </div>
                <div className="finance-calculator-container">
                    <FinanceCalculator />
                </div>
            </div>

            <div className="finance-page-row finance-page-row--usage-and-help l-capped-width u-margin-bottom--l">
                <div className="finance-usage u-margin-bottom--l">
                    <h3 className="finance-page__header finance-usage__header">
                        {easyWaysHeader}
                    </h3>
                    <div className="finance-usage__usages">
                        <div className={financeUseClass}>
                            <div className="finance-use__mobile-content--left">
                                <div className="finance-use__image finance-use__image--mobile">
                                    <WebpageIcon
                                        description={shoppingDescription}
                                        title={shoppingIconTitle}
                                    />
                                </div>
                            </div>
                            <div className="finance-use__mobile-content--right">
                                <h4 className="finance-use__title">
                                    {shoppingTitle}
                                </h4>
                                <div className="finance-use__image">
                                    <WebpageIcon
                                        description={shoppingDescription}
                                        title={shoppingIconTitle}
                                    />
                                </div>
                                <a
                                    href={`${props.shop_mattresses_url}all/`}
                                    className="finance-use__cta al-financing-page__three-ways--shop-mattresses"
                                >
                                    {t`Shop Mattresses`}
                                </a>
                            </div>
                        </div>
                        <div className={financeUseClass}>
                            <div className="finance-use__mobile-content--left">
                                <div className="finance-use__image finance-use__image--mobile">
                                    <PhoneIcon
                                        description={talkToDescription}
                                        title={phoneIconTitle}
                                    />
                                </div>
                            </div>
                            <div className="finance-use__mobile-content--right">
                                <h4 className="finance-use__title">
                                    {t`Talking to a Sleep Expert`}
                                </h4>
                                <div className="finance-use__image">
                                    <PhoneIcon
                                        description={talkToDescription}
                                        title={phoneIconTitle}
                                    />
                                </div>
                                <a
                                    href={props.phone_href}
                                    className="finance-use__cta al-financing-page__three-ways--call-expert"
                                >
                                    {props.phone_display}
                                </a>
                            </div>
                        </div>
                        {!isSale2 && (
                            <div className="finance-use">
                                <div className="finance-use__mobile-content--left">
                                    <div className="finance-use__image finance-use__image--mobile">
                                        <StoreDoorIcon
                                            description={
                                                visitClosestDescription
                                            }
                                            title={locationIconTitle}
                                        />
                                    </div>
                                </div>
                                <div className="finance-use__mobile-content--right">
                                    <h4 className="finance-use__title">
                                        {t`Visiting your closest Tempur-Pedic Store`}
                                    </h4>
                                    <div className="finance-use__image">
                                        <StoreDoorIcon
                                            description={
                                                visitClosestDescription
                                            }
                                            title={locationIconTitle}
                                        />
                                    </div>
                                    <DistanceToStoreSSR />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="finance-help">
                    <h5 className="finance-help__title">
                        {t`Need help with your existing Wells Fargo Account?`}
                    </h5>
                    <div className="finance-help__content">
                        <a
                            href="https://retailservices.wellsfargo.com/customer/index.html?returnFull=1"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="button button--inverse finance-help__button"
                        >
                            {t`Access your Account`}
                        </a>
                        {/* Single `{% blocktrans %}` with an embedded link in
                            the legacy template -> kept as one translatable
                            sentence via `jt` (codebase convention). Unlike
                            sealy, the tempurpedic legacy markup had NO <span>
                            wrapper around this sentence. */}
                        {jt`Contact Wells Fargo Bank, N.A. at 877-805-7744 or sign on at ${wellsFargoLink} to manage your account.`}
                        <a
                            href="https://wellsfargo.com/cardholders"
                            className="finance-help__link al-financing-page__acccess-your-account"
                        >
                            {t`Access your Account`}
                        </a>
                    </div>
                </div>
            </div>

            <div className="finance-page__pre-approval-ad--mobile finance-page__pre-approval-ad--mobile-alternate pre-approval-ad pre-approval-ad--finance-page u-flex-container">
                <div className="pre-approval-ad__or-and-see">
                    <div className="pre-approval-ad__see">
                        <div className="pre-approval-ad__see--bold">
                            {t`Check if you're pre-qualified`}
                        </div>
                        <div className="pre-approval-ad__see--small">
                            {t`without affecting your credit score.`}
                        </div>
                    </div>
                </div>
                <FinancingModalTriggerCheckNow classString="button pre-approval-ad__button pre-approval-ad__button--mobile pre-approval-ad__check" />
                <div className="pre-approval-ad__apply-alternate">
                    {t`or`}{" "}
                    <FinancingModalTrigger
                        modalType="full-app"
                        className="pre-approval-ad__apply-alternate-link"
                        title={applyNowText}
                        applicationSource={APPLICATION_SOURCE}
                    >
                        {applyNowText}
                    </FinancingModalTrigger>
                </div>
            </div>

            <div className="finance-page-row finance-page-row--faqs l-capped-width u-margin-bottom--xl">
                {!isSale2 &&
                    promotions.map((block) => (
                        // The legacy default template rendered the promotion
                        // loop INSIDE the FAQ row, before its header, each
                        // promotion wrapped in `div.ohter-financing` (a legacy
                        // typo class, preserved verbatim -- contrast sealy's
                        // own `.finance-promotion` row). The sale2 template
                        // had no promotion loop at all.
                        <div key={block.id} className="ohter-financing">
                            {/* GA4 parity: NO `blockIndex` (defaults to 1) --
                                the legacy include_block path passed no index,
                                so creative_slot was ALWAYS
                                "promotion-block-1". */}
                            <PromotionBlock value={block.value} />
                        </div>
                    ))}
                <h3 className="finance-page__header finance-faqs__header">
                    {t`Frequently Asked Questions`}
                </h3>
                {faqs.map((block) => (
                    <div key={block.id} className="finance-faqs__faqs">
                        <TempurFinanceFAQsBlock value={block.value} />
                    </div>
                ))}
            </div>

            <div
                className="finance-page-row finance-page__terms l-capped-width u-margin-bottom--l"
                id="terms_and_conditions"
                dangerouslySetInnerHTML={{ __html: props.terms_html }}
            />

            <div className="finance-page__pre-approval-ads l-capped-width u-margin-bottom--l">
                <FinancingModalTriggerFinance
                    ccSource={financingCCs.TEMPUR}
                    applicationSource={APPLICATION_SOURCE}
                />
            </div>
        </>
    );
};
